import { AutomationTypes } from './automation-services'

export interface AutomationEventGroup {
  title: string
  event: AutomationEventTypes
  tooltip?: string
  icon?: string
  color?: string
  types?: AutomationTypes[]
}

export enum AutomationEventTypes {
  // Booking
  InquiryCreated = 'inquiry.create',
  BookingConfirmed = 'inquiry.book',
  BookingCanceled = 'inquiry.cancel',

  BookingDatesUpdated = 'inquiry.update.dates',
  BookingRentalUpdated = 'inquiry.update.rental',
  BookingIsPaid = 'inquiry.update.paid',
  BookingCostUpdated = 'inquiry.update.cost',
  BookingGuestUpdated = 'inquiry.update.guest',

  // Invoice
  InvoiceCreated = 'invoice.create',
  InvoicePaid = 'invoice.paid',
  InvoiceCanceled = 'invoice.cancel',

  HoldCreated = 'event.create',
  HoldUpdated = 'event.update',
  HoldDeleted = 'event.delete',

  // Rental Access
  RentalAccessCreated = 'rentalaccess.created',
  RentalAccessUpdated = 'rentalaccess.updated',

  // Device
  DeviceConnected = 'device.connected',
  DeviceLowBattery = 'device.notification.low_battery',
  DeviceCodeFirstUsed = 'device.notification.code_first_used',
  DeviceIssuesRequireAttention = 'device.notification.issues_require_attention',

  DeviceAccessCreated = 'smartdeviceaccess.created',
  DeviceAccessUpdated = 'smartdeviceaccess.updated',

  // Card
  PaymentCardAccepted = 'payment.card.accepted',
  PaymentCardDeclined = 'payment.card.declined',
  PaymentCardDeleted = 'payment.card.deleted',
  PaymentCardRejected = 'payment.card.rejected',

  // Contract
  ContractSent = 'contract.sent',
  ContractSigned = 'contract.signed',
  ContractRejected = 'contract.rejected',
  ContractViewed = 'contract.viewed',

  // Guest
  GuestCreated = 'guest.create',
  GuestUpdated = 'guest.update',
  GuestDeleted = 'guest.delete',

  // Message sent to guest
  GuestMessageCreated = 'message.create',

  ChannelDisabled = 'system.error.channel.disabled',

  // Extra
  ExtraCreated = 'extra.create',
  ExtraUpdated = 'extra.update',
  ExtraDeleted = 'extra.delete',
  ExtraPaid = 'extra.paid',

  // Charge
  ChargeCreated = 'charge.create',
  ChargePaid = 'charge.success',
  ChargeFailed = 'charge.fail',
  ChargeDeleted = 'charge.delete',

  // Expense
  ExpenseCreated = 'expense.create',
  ExpensePaid = 'expense.paid',
  ExpenseApproved = 'expense.approve',
  ExpenseDenied = 'expense.deny',
  ExpenseDeleted = 'expense.delete',

  // Task
  TaskCreated = 'task.create',
  TaskCompleted = 'task.complete',
  TaskReassigned = 'task.reassign',
  TaskOverdue = 'task.overdue',

  // Incident
  IncidentCreatedAI = 'incident.ai.create',
  IncidentCreated = 'incident.create',
  IncidentUpdated = 'incident.update',
  IncidentDeleted = 'incident.delete',
  IncidentEscalated = 'incident.escalate',
  IncidentCritical = 'incident.critical',
  IncidentResolved = 'incident.resolve',
  IncidentOnHold = 'incident.hold',

  // Inventory
  InventoryCreated = 'inventory.create',
  InventoryUpdated = 'inventory.update',
  InventoryDeleted = 'inventory.delete',
  InventoryDamaged = 'inventory.damage',
  InventoryInoperable = 'inventory.inoperable',
  InventoryDepleted = 'inventory.depleted',

  // Security Deposit
  SecurityDepositCreated = 'security_deposit.created',
  SecurityDepositAuthorized = 'security_deposit.authorized',
  SecurityDepositCaptured = 'security_deposit.captured',
  SecurityDepositRefunded = 'security_deposit.refunded',
  SecurityDepositCanceled = 'security_deposit.canceled',

  // Review
  ReviewHostSubmitted = 'review.submitted.host',
  ReviewPending = 'review.pending',
  ReviewGuestSubmitted = 'review.submitted.guest',

  // Rates
  RateCreated = 'rate.created',

  // Time-based
  TimeBasedEvent = 'time',
  TimeAfterInquiryCreated = 'time.after.inquiry.created',
  TimeAfterBookingConfirm = 'time.after.booking.confirm',
  TimeAfterBookingCancel = 'time.after.booking.cancel',

  TimeBeforeBookingCheckIn = 'time.before.booking.start',
  TimeAfterBookingCheckIn = 'time.after.booking.start',

  TimeBeforeBookingCheckout = 'time.before.booking.end',
  TimeAfterBookingCheckout = 'time.after.booking.end',

  TimeBeforeInvoiceDue = 'time.before.invoice.due',
  TimeAfterInvoiceDue = 'time.after.invoice.due',
}

export const automationEvents: AutomationEventGroup[] = [
  {
    title: $localize`Inquiry Created`,
    event: AutomationEventTypes.InquiryCreated,
    tooltip: $localize`Inquiry is created via the dashboard, a AdvanceCM website or widget, or an API channel connection.`,
    icon: 'cs-icon cs-icon-booking-created',
    color: 'text-danger',
    types: [
      //
      AutomationTypes.Message,
      AutomationTypes.Task,
      AutomationTypes.Expense,
      AutomationTypes.Invoice,
      AutomationTypes.Charge,
      AutomationTypes.Booking,
      AutomationTypes.Document,
    ],
  },
  {
    title: $localize`Booking Confirmed`,
    event: AutomationEventTypes.BookingConfirmed,
    tooltip: $localize`Booking confirmed via dashboard or connected API channel.`,
    icon: 'cs-icon cs-icon-booking-confirmed',
    color: 'text-indigo',
    types: [
      //
      AutomationTypes.Message,
      AutomationTypes.Task,
      AutomationTypes.Expense,
      AutomationTypes.Invoice,
      AutomationTypes.Charge,
      AutomationTypes.Booking,
      AutomationTypes.Document,
    ],
  },
  {
    title: $localize`Booking Cancelled`,
    event: AutomationEventTypes.BookingCanceled,
    tooltip: $localize`Booking cancelled via dashboard or connected API channel.`,
    icon: 'cs-icon cs-icon-booking-canceled',
    color: 'text-info',
    types: [
      //
      AutomationTypes.Message,
      AutomationTypes.Task,
      AutomationTypes.Expense,
      AutomationTypes.Invoice,
      AutomationTypes.Charge,
      AutomationTypes.Booking,
    ],
  },

  {
    title: $localize`Booking Cost Changed`,
    event: AutomationEventTypes.BookingCostUpdated,
  },

  {
    title: $localize`Booking is Paid`,
    event: AutomationEventTypes.BookingIsPaid,
  },
  {
    title: $localize`Booking Guest Changed`,
    event: AutomationEventTypes.BookingGuestUpdated,
  },
  {
    title: $localize`Booking Dates Changed`,
    event: AutomationEventTypes.BookingDatesUpdated,
  },
  {
    title: $localize`Booking Rental Changed`,
    event: AutomationEventTypes.BookingRentalUpdated,
  },
  {
    title: 'Hold Event Created',
    event: AutomationEventTypes.HoldCreated,
    icon: 'cs-icon cs-icon-calendar-pause2',
    color: 'text-success',
    types: [AutomationTypes.Message],
  },
  {
    title: 'Hold Event Updated',
    event: AutomationEventTypes.HoldUpdated,
    icon: 'cs-icon cs-icon-calendar-pencil',
    color: 'text-info',
    types: [AutomationTypes.Message],
  },
  {
    title: 'Hold Event Deleted',
    event: AutomationEventTypes.HoldDeleted,
    icon: 'cs-icon cs-icon-calendar-times',
    color: 'text-danger',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Invoice Created`,
    event: AutomationEventTypes.InvoiceCreated,
    tooltip: $localize`Invoice created on any AdvanceCM inquiry.`,
    icon: 'cs-icon cs-icon-invoice-created',
    color: 'text-primary',
    types: [
      AutomationTypes.Message,
      AutomationTypes.Booking,
      AutomationTypes.Task,
      AutomationTypes.Expense,
      AutomationTypes.Document,
    ],
  },
  {
    title: $localize`Invoice Paid`,
    event: AutomationEventTypes.InvoicePaid,
    tooltip: $localize`Invoice status changes to paid on any AdvanceCM invoice.`,
    icon: 'cs-icon cs-icon-invoice-paid',
    color: 'text-success',
    types: [
      AutomationTypes.Message,
      AutomationTypes.Booking,
      AutomationTypes.Task,
      AutomationTypes.Expense,
      AutomationTypes.Document,
    ],
  },
  {
    title: $localize`Invoice Cancelled`,
    event: AutomationEventTypes.InvoiceCanceled,
    tooltip: $localize`Any AdvanceCM invoice is marked as canceled.`,
    icon: 'cs-icon cs-icon-invoice-canceled',
    color: 'text-danger',
    types: [AutomationTypes.Message, AutomationTypes.Booking, AutomationTypes.Task, AutomationTypes.Expense],
  },
  {
    title: $localize`Guest Created`,
    event: AutomationEventTypes.GuestCreated,
    tooltip: $localize`Guest is created either via the dashboard, from a AdvanceCM website or widget, or from a channel connection.`,
    icon: 'cs-icon cs-icon-guest-created',
    color: 'text-primary',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Guest Updated`,
    event: AutomationEventTypes.GuestUpdated,
    tooltip: $localize`Guest is updated via the dashboard or a channel connection.`,
    icon: 'cs-icon cs-icon-guest-updated',
    color: 'text-success',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Guest Deleted`,
    event: AutomationEventTypes.GuestDeleted,
    tooltip: $localize`Guest is deleted from the dashboard.`,
    icon: 'cs-icon cs-icon-guest-deleted',
    color: 'text-danger',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Guest Message`,
    event: AutomationEventTypes.GuestMessageCreated,
    tooltip: $localize`Message is received from a guest.`,
    icon: 'cs-icon cs-icon-message-received',
    color: 'text-primary',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Channel Disabled`,
    event: AutomationEventTypes.ChannelDisabled,
    tooltip: $localize`When access is revoked by originating channel.`,
    icon: 'cs-icon cs-icon-channel-disabled',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Card Accepted`,
    event: AutomationEventTypes.PaymentCardAccepted,
    tooltip: $localize`Credit card info submitted to Stripe has been accepted.`,
    icon: 'cs-icon cs-icon-card-accepted',
    color: 'text-success',
    types: [AutomationTypes.Message, AutomationTypes.Booking, AutomationTypes.Task, AutomationTypes.Expense],
  },
  {
    title: $localize`Card Rejected`,
    event: AutomationEventTypes.PaymentCardRejected,
    tooltip: $localize`Credit card info submitted to Stripe has been rejected.`,
    icon: 'cs-icon cs-icon-card-rejected',
    color: 'text-danger',
    types: [AutomationTypes.Message, AutomationTypes.Booking, AutomationTypes.Task, AutomationTypes.Expense],
  },
  {
    title: $localize`Card Declined`,
    event: AutomationEventTypes.PaymentCardDeclined,
    tooltip: $localize`Credit card has been declined after Stripe attempts processing.`,
    icon: 'cs-icon cs-icon-card-declined',
    color: 'text-warning',
    types: [AutomationTypes.Message, AutomationTypes.Booking, AutomationTypes.Task, AutomationTypes.Expense],
  },
  {
    title: $localize`Card Deleted`,
    event: AutomationEventTypes.PaymentCardDeleted,
    tooltip: $localize`Credit card has been deleted from Stripe.`,
    icon: 'cs-icon cs-icon-card-deleted',
    color: 'text-indigo',
    types: [AutomationTypes.Message, AutomationTypes.Booking, AutomationTypes.Task, AutomationTypes.Expense],
  },
  {
    title: $localize`Contract Sent`,
    event: AutomationEventTypes.ContractSent,
    tooltip: $localize`A Signature Contract has been sent.`,
    icon: 'cs-icon cs-icon-contract-sent',
    color: 'text-primary',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Contract Viewed`,
    event: AutomationEventTypes.ContractViewed,
    tooltip: $localize`A Signature Contract has been opened by a signer after sending.`,
    icon: 'cs-icon cs-icon-contract-viewed',
    color: 'text-info',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Contract Signed`,
    event: AutomationEventTypes.ContractSigned,
    tooltip: $localize`A Signature Contract has been signed.`,
    icon: 'cs-icon cs-icon-contract-signed',
    color: 'text-success',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Contract Rejected`,
    event: AutomationEventTypes.ContractRejected,
    tooltip: $localize`A Signature Contract has been rejected.`,
    icon: 'cs-icon cs-icon-contract-rejected',
    color: 'text-danger',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Smart Device Connected`,
    event: AutomationEventTypes.DeviceConnected,
  },
  {
    title: $localize`Rental Access Created`,
    event: AutomationEventTypes.RentalAccessCreated,
    tooltip: $localize`Rental access is created: door code, Wifi password, etc.`,
    icon: 'cs-icon cs-icon-house-hashtag',
    color: 'text-primary',
    types: [AutomationTypes.Message, AutomationTypes.Booking],
  },
  {
    title: $localize`Rental Access Updated`,
    event: AutomationEventTypes.RentalAccessUpdated,
    tooltip: $localize`Rental access is updated: door code, Wifi password, etc.`,
    icon: 'cs-icon cs-icon-house-lock',
    color: 'text-info',
    types: [AutomationTypes.Message, AutomationTypes.Booking],
  },
  {
    title: $localize`Smart Device Code Generated`,
    event: AutomationEventTypes.DeviceAccessCreated,
    tooltip: $localize`Messages for Any code generated through Smart Devices will be sent from here`,
    icon: 'cs-icon cs-icon-smart-lock2',
    color: 'text-primary',
    types: [AutomationTypes.Message, AutomationTypes.Device],
  },
  {
    title: $localize`Smart Device Code Updated`,
    event: AutomationEventTypes.DeviceAccessUpdated,
    tooltip: $localize`Information regarding code changes (Dates, time etc) will be send through this event`,
    icon: 'cs-icon cs-icon-smart-lock',
    color: 'text-info',
    types: [AutomationTypes.Message, AutomationTypes.Device],
  },
  {
    title: $localize`Device Battery is Low`,
    event: AutomationEventTypes.DeviceLowBattery,
    tooltip: $localize`Device battery is low.`,
    icon: 'far fa-user-lock',
    color: 'text-warning',
    types: [AutomationTypes.Device],
  },
  {
    title: $localize`Device Code is first used`,
    event: AutomationEventTypes.DeviceCodeFirstUsed,
    tooltip: $localize`Device Code is first used`,
    icon: 'far fa-user-lock',
    color: 'text-info',
    types: [AutomationTypes.Device],
  },
  {
    title: $localize`Device has Issues`,
    event: AutomationEventTypes.DeviceIssuesRequireAttention,
    tooltip: $localize`Device has Issues needs attention`,
    icon: 'far fa-user-lock',
    color: 'text-danger',
    types: [AutomationTypes.Device],
  },
  {
    title: $localize`Extra Created`,
    event: AutomationEventTypes.ExtraCreated,
  },
  {
    title: $localize`Extra Updated`,
    event: AutomationEventTypes.ExtraUpdated,
  },
  {
    title: $localize`Extra Deleted`,
    event: AutomationEventTypes.ExtraDeleted,
  },
  {
    title: $localize`Extra Paid`,
    event: AutomationEventTypes.ExtraPaid,
  },
  {
    title: $localize`Charge Created`,
    event: AutomationEventTypes.ChargeCreated,
  },
  {
    title: $localize`Charge Paid`,
    event: AutomationEventTypes.ChargePaid,
  },
  {
    title: $localize`Charge Payment Failed`,
    event: AutomationEventTypes.ChargeFailed,
  },
  {
    title: $localize`Charge Deleted`,
    event: AutomationEventTypes.ChargeDeleted,
  },

  {
    title: $localize`Expense Created`,
    event: AutomationEventTypes.ExpenseCreated,
  },
  {
    title: $localize`Expense Paid`,
    event: AutomationEventTypes.ExpensePaid,
  },
  {
    title: $localize`Expense Approved`,
    event: AutomationEventTypes.ExpenseApproved,
  },
  {
    title: $localize`Expense Denied`,
    event: AutomationEventTypes.ExpenseDenied,
  },

  {
    title: $localize`Expense Deleted`,
    event: AutomationEventTypes.ExpenseDeleted,
  },

  {
    title: $localize`Task Created`,
    event: AutomationEventTypes.TaskCreated,
  },
  {
    title: $localize`Task Completed`,
    event: AutomationEventTypes.TaskCompleted,
  },
  {
    title: $localize`Task Reassigned`,
    event: AutomationEventTypes.TaskReassigned,
  },
  {
    title: $localize`Task Overdue`,
    event: AutomationEventTypes.TaskOverdue,
  },

  {
    title: $localize`Incident Created`,
    event: AutomationEventTypes.IncidentCreated,
  },
  {
    title: $localize`Incident Updated`,
    event: AutomationEventTypes.IncidentUpdated,
  },
  {
    title: $localize`Incident Deleted`,
    event: AutomationEventTypes.IncidentDeleted,
  },
  {
    title: $localize`Incident Escalated`,
    event: AutomationEventTypes.IncidentEscalated,
  },
  {
    title: $localize`Incident Critical`,
    event: AutomationEventTypes.IncidentCritical,
  },
  {
    title: $localize`Incident Resolved`,
    event: AutomationEventTypes.IncidentResolved,
  },
  {
    title: $localize`Incident On Hold`,
    event: AutomationEventTypes.IncidentOnHold,
  },

  {
    title: $localize`Host Submitted Review`,
    event: AutomationEventTypes.ReviewHostSubmitted,
  },
  {
    title: $localize`Review Pending`,
    event: AutomationEventTypes.ReviewPending,
  },
  {
    title: $localize`Received Guest Review`,
    event: AutomationEventTypes.ReviewGuestSubmitted,
  },

  {
    title: $localize`Inventory Created`,
    event: AutomationEventTypes.InventoryCreated,
  },
  {
    title: $localize`Inventory Updated`,
    event: AutomationEventTypes.InventoryUpdated,
  },
  {
    title: $localize`Inventory Deleted`,
    event: AutomationEventTypes.InventoryDeleted,
  },
  {
    title: $localize`Inventory Damaged`,
    event: AutomationEventTypes.InventoryDamaged,
  },
  {
    title: $localize`Inventory Inoperable`,
    event: AutomationEventTypes.InventoryInoperable,
  },
  {
    title: $localize`Inventory Depleted`,
    event: AutomationEventTypes.InventoryDepleted,
  },
  {
    title: $localize`Security Deposit Created`,
    event: AutomationEventTypes.SecurityDepositCreated,
    tooltip: $localize`A security deposit request is created for a booking.`,
    icon: 'cs-icon cs-icon-shield-check',
    color: 'text-info',
    types: [AutomationTypes.SecurityDeposit],
  },
  {
    title: $localize`Security Deposit Authorized`,
    event: AutomationEventTypes.SecurityDepositAuthorized,
    tooltip: $localize`A security deposit authorization has been successfully held.`,
    icon: 'cs-icon cs-icon-shield-check',
    color: 'text-success',
    types: [AutomationTypes.SecurityDeposit],
  },
  {
    title: $localize`Security Deposit Captured`,
    event: AutomationEventTypes.SecurityDepositCaptured,
    tooltip: $localize`Security deposit funds have been captured from the guest.`,
    icon: 'cs-icon cs-icon-dollar-square',
    color: 'text-warning',
    types: [AutomationTypes.SecurityDeposit],
  },
  {
    title: $localize`Security Deposit Refunded`,
    event: AutomationEventTypes.SecurityDepositRefunded,
    tooltip: $localize`Captured security deposit funds have been refunded to the guest.`,
    icon: 'cs-icon cs-icon-dollar-cycle',
    color: 'text-success',
    types: [AutomationTypes.SecurityDeposit],
  },
  {
    title: $localize`Security Deposit Canceled`,
    event: AutomationEventTypes.SecurityDepositCanceled,
    tooltip: $localize`A security deposit authorization has been canceled or released.`,
    icon: 'cs-icon cs-icon-shield-check',
    color: 'text-danger',
    types: [AutomationTypes.SecurityDeposit],
  },

  {
    title: $localize`Time-based Event`,
    event: AutomationEventTypes.TimeBasedEvent,
    tooltip: '',
    icon: 'cs-icon cs-icon-calendar-clock2',
    color: 'text-success',
    types: [
      //
      AutomationTypes.Message,
      AutomationTypes.Invoice,
      AutomationTypes.Charge,
      AutomationTypes.Booking,
      AutomationTypes.Document,
      AutomationTypes.Review,
      AutomationTypes.Task,
    ],
  },
]

export const automationTimeEvents: AutomationEventGroup[] = [
  {
    title: $localize`After Inquiry Created`,
    event: AutomationEventTypes.TimeAfterInquiryCreated,
  },
  {
    title: $localize`After Booking Confirmed`,
    event: AutomationEventTypes.TimeAfterBookingConfirm,
  },
  {
    title: $localize`After Booking Cancelled`,
    event: AutomationEventTypes.TimeAfterBookingCancel,
  },
  {
    title: $localize`Before Check-in`,
    event: AutomationEventTypes.TimeBeforeBookingCheckIn,
  },
  {
    title: $localize`After Check-in`,
    event: AutomationEventTypes.TimeAfterBookingCheckIn,
  },
  {
    title: $localize`Before Check-out`,
    event: AutomationEventTypes.TimeBeforeBookingCheckout,
  },
  {
    title: $localize`After Check-out`,
    event: AutomationEventTypes.TimeAfterBookingCheckout,
  },
  {
    title: $localize`Before Invoice Due`,
    event: AutomationEventTypes.TimeBeforeInvoiceDue,
  },
  {
    title: $localize`After Invoice Due`,
    event: AutomationEventTypes.TimeAfterInvoiceDue,
  },
]

export function getAvailableAutomationEvents(type: AutomationTypes, includeTimeEvents = false) {
  const items = automationEvents.filter((e) => e.types?.includes(type))
  const isTimeEventIncluded = items.find((t) => t.event === AutomationEventTypes.TimeBasedEvent)
  if (isTimeEventIncluded && includeTimeEvents) {
    return [...items, ...automationTimeEvents]
  } else {
    return items
  }
}

export function getAutomationEventName(event: string) {
  const e = automationEvents.find((t) => t.event === event)
  if (e) return e.title
  return automationTimeEvents.find((t) => t.event === event)?.title
}

export const automationStatuses = [
  {
    name: $localize`Active`,
    key: 'active',
  },
  {
    name: $localize`All Unarchived`,
    key: 'unarchived',
  },
  {
    name: $localize`Archived`,
    key: 'archived',
  },
  {
    name: $localize`Paused`,
    key: 'paused',
  },
]
